/**
 * 바로 보내기 — 고른 화면에 사진·영상을 즉시 띄운다.
 *
 * 이 기능이 필요한 순간은 대개 급하다: "지금 로비 화면에 이 안내 띄워 주세요."
 * 그래서 **파일을 끌어다 놓는 것에서 전송까지 한 화면**에서 끝내고, 편성·플레이리스트
 * 같은 개념을 요구하지 않는다.
 *
 * **플레이리스트도 보낼 수 있다**(2026-10-28 사용자 지시 "바로 보내기에 플레이리스트가 없으면 추가해"). 위쪽
 * [콘텐츠 | 플레이리스트] 에서 고르면 그 플레이리스트의 켜 둔 항목이 **보내는 순간의 순서·노출 시간·전환 그대로**
 * 나간다 — 뒤에 플레이리스트를 고쳐도 이 바로 보내기는 그대로다(서버가 숨은 플레이리스트에 옮겨 둔다).
 *
 * 편성은 건드리지 않는다. 해제하면 원래 편성으로 그대로 돌아온다.
 */
import { useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { FileCode, FileText, FileUp, Image as ImageIcon, ListMusic, Loader2, Play, Video, X } from 'lucide-react';
import type { Asset, Playlist, SignageOneClient } from '../api/client.js';
import { HelpTip } from './HelpTip.js';
import { AssetThumbSmall } from './AssetThumbSmall.js';
import { DateTimeField } from './DateField.js';
import { Badge, ErrorNote, formatBytes, Modal, relativeTime, RelTime } from './ui.js';
import { useAction, useAsync, useDebounced } from '../hooks.js';
import { StorageWarning, useStorageCheck } from './StorageWarning.js';
import { allVideo, type ContentKind } from '../content-rules.js';
import { Disclosure } from './Disclosure.js';
import { isPdfFile, pdfToPages } from '../pdfPages.js';
import { focusFirstInvalid, NumberInput, numberProblem, type NumberRule } from './form/NumberInput.js';

/** 종료 시각 선택지. 현장에서 실제로 쓰는 단위만 둔다. */
type UntilChoice = 'forever' | '1h' | '4h' | 'today' | 'custom';

const UNTIL_LABEL: Record<UntilChoice, string> = {
  forever: '해제할 때까지',
  '1h': '1시간',
  '4h': '4시간',
  today: '오늘 자정까지',
  custom: '직접 지정',
};

/** 고를 수 있는 종류와 그 이름. 탭과 빈 상태가 **같은 말**을 쓰도록 한 곳에 둔다. */
const KIND_LABEL = {
  all: '전체',
  image: '사진',
  video: '동영상',
  template: '템플릿',
  web: '웹',
  html: 'HTML',
} as const;

export function DirectSendModal({
  client,
  targets,
  skipped = [],
  onClose,
  onSent,
}: {
  client: SignageOneClient;
  /** 보낼 대상 화면(켜진 것). 하나면 이름을, 여럿이면 대수를 보여준다. */
  targets: Array<{ id: string; name: string }>;
  /**
   * 고른 화면 중 꺼진 것. 말없이 빼면 보낸 사람은 전부 나간 줄 안다 — 이름을 적어 두고, **걸어 둘지**
   * 고르게 한다. 걸어 두면 다시 연결될 때(끝낼 시각 전이면) 나가고, 그 전까지 화면 상세에
   * '전달 대기' 로 남는다(서버의 `queue_offline`).
   */
  skipped?: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSent: () => void;
}): ReactNode {
  const [picked, setPicked] = useState<Asset[]>([]);
  /** 무엇을 보낼까 — 콘텐츠를 골라서, 또는 플레이리스트 하나를 통째로. */
  const [source, setSource] = useState<'content' | 'playlist'>('content');
  const [pickedPlaylist, setPickedPlaylist] = useState<Playlist | null>(null);
  /** 한 장당 노출 시간. 비운 동안은 null — 지우면 15 로 되돌아가던 칸이다. */
  const [dwell, setDwell] = useState<number | null>(15);
  /** [보내기] 를 눌렀는데 노출 시간 칸을 고쳐야 했다 — 그 칸을 짚는다(고치면 바로 풀린다). */
  const [showErrors, setShowErrors] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [until, setUntil] = useState<UntilChoice>('forever');
  const [customUntil, setCustomUntil] = useState('');
  const [note, setNote] = useState('');
  /** 꺼진 화면에도 걸어 둔다. 고른 화면이 모두 꺼져 있으면 이것 말고는 보낼 길이 없으니 켜 둔다. */
  const [queueOffline, setQueueOffline] = useState(targets.length === 0 && skipped.length > 0);
  const [search, setSearch] = useState('');
  /**
   * 어떤 종류를 볼 것인가.
   *
   * 사진·동영상만 나눠 두었더니 **템플릿과 웹은 "전체" 안에서만 보였다** — 공지 템플릿
   * 하나를 급히 띄우려는 사람이 콘텐츠 수십 건을 훑어야 했다. 콘솔이 다루는 네 종류를
   * 그대로 낸다(`content-rules.ts` 의 `ContentKind`).
   */
  const [kind, setKind] = useState<'all' | Asset['type']>('all');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState<string[]>([]);

  const action = useAction();
  const query = useDebounced(search.trim(), 250);
  const library = useAsync(
    () => client.allAssets({ archived: 'false', q: query, ...(kind === 'all' ? {} : { type: kind }) }),
    [client, query, kind],
  );

  // 플레이리스트 쪽을 열 때만 받는다 — 콘텐츠만 보내는 사람에게 목록 두 벌을 받게 하지 않는다.
  const playlists = useAsync(
    () => (source === 'playlist' ? client.allPlaylists() : Promise.resolve(null)),
    [client, source],
  );
  /** 플레이리스트 항목의 이름 — 오른쪽 "보낼 순서" 에 적는다(보관된 것까지). */
  const allAssets = useAsync(
    () => (source === 'playlist' ? client.allAssets({ archived: 'all' }) : Promise.resolve(null)),
    [client, source],
  );
  const assetById = useMemo(() => new Map((allAssets.data?.items ?? []).map((asset) => [asset.id, asset])), [allAssets.data]);
  const shownPlaylists = (playlists.data?.items ?? []).filter(
    (playlist) => !playlist.archived && (!query || playlist.name.toLowerCase().includes(query.toLowerCase())),
  );
  /** 플레이리스트로 보낼 때 나갈 줄 — 켜 둔 것만(서버도 같은 기준이고, 쓸 수 없는 콘텐츠는 서버가 뺀다). */
  const playlistItems = (pickedPlaylist?.items ?? []).filter((item) => item.enabled);
  const byPlaylist = source === 'playlist';

  const pickedIds = useMemo(() => new Set(picked.map((asset) => asset.id)), [picked]);
  /**
   * 고른 것이 모두 영상인가 — 그러면 노출 시간 칸이 쓰일 곳이 없다. 영상은 노출 시간과 상관없이 끝까지
   * 재생한 뒤 넘어간다. 섞여 있으면 칸은 사진·템플릿·웹에만 쓰인다.
   */
  const onlyVideo = allVideo(picked);
  const someVideo = picked.some((asset) => asset.type === 'video');
  /** 노출 시간 칸의 문제 — 모두 영상이거나 플레이리스트로 보내면 칸이 없다. */
  const dwellError = onlyVideo || byPlaylist ? null : numberProblem(dwell, DWELL_RULE);

  const addFiles = (files: FileList | File[]): void => {
    const list = [...files].filter((file) => file.size > 0);
    if (list.length === 0) return;

    setUploading((current) => [...current, ...list.map((file) => file.name)]);
    void action.run(
      async () => {
        const uploaded: Asset[] = [];
        // 하나씩 올린다. 동시에 올리면 큰 파일에서 서버·회선이 함께 막힌다.
        for (const file of list) {
          try {
            if (isPdfFile(file)) {
              // PDF 는 쪽마다 그림으로 바꿔 쪽 순서대로 고른다(`pdfPages.ts`) — 보내면 그 순서로 넘어간다.
              for (const page of await pdfToPages(file)) uploaded.push(await client.uploadAsset(page.file, [], page.title));
            } else {
              uploaded.push(await client.uploadAsset(file));
            }
          } finally {
            setUploading((current) => current.filter((name) => name !== file.name));
          }
        }
        return uploaded;
      },
      (uploaded) => {
        // 같은 파일을 다시 올리면 서버가 기존 자산을 돌려준다(중복 제거).
        setPicked((current) => {
          const seen = new Set(current.map((asset) => asset.id));
          return [...current, ...uploaded.filter((asset) => !seen.has(asset.id))];
        });
        library.reload();
      },
    );
  };

  const toggle = (asset: Asset): void => {
    setPicked((current) =>
      current.some((item) => item.id === asset.id)
        ? current.filter((item) => item.id !== asset.id)
        : [...current, asset],
    );
  };

  const send = (): void => {
    const untilIso = resolveUntil(until, customUntil);
    if (until === 'custom' && !untilIso) return;
    if (dwellError) {
      setShowErrors(true);
      focusFirstInvalid(formRef.current);
      return;
    }
    setShowErrors(false);

    // 모두 영상이면 노출 시간을 싣지 않는다(`directDwell`) — 그때는 칸이 없어 비었을 수 있다.
    // 플레이리스트면 그 플레이리스트에 정한 노출 시간 그대로 — 싣지 않는다.
    const what = byPlaylist && pickedPlaylist
      ? { playlist_id: pickedPlaylist.id }
      : { asset_ids: picked.map((asset) => asset.id), ...directDwell(picked, dwell ?? 15) };

    // 걸어 두기로 했으면 꺼진 화면도 대상에 넣는다.
    const all = queueOffline ? [...targets, ...skipped] : targets;
    void action.run<unknown>(
      () =>
        all.length === 1
          ? client.sendDirect(all[0]!.id, {
              ...what,
              until: untilIso,
              note: note.trim(),
              queue_offline: queueOffline,
            })
          : client.sendDirectBulk({
              display_ids: all.map((target) => target.id),
              ...what,
              until: untilIso,
              note: note.trim(),
              queue_offline: queueOffline,
            }),
      onSent,
    );
  };

  const sendCount = targets.length + (queueOffline ? skipped.length : 0);
  /*
   * 고른 콘텐츠가 보낼 화면들의 저장 공간에 들어가는가 — 큰 영상을 급히 보낼 때 가장 흔히 넘친다.
   * 막지 않는다. 받을 화면(걸어 두기로 한 꺼진 화면 포함)만 묻는다.
   */
  const recipients = queueOffline ? [...targets, ...skipped] : targets;
  const sendingAssetIds = byPlaylist ? [...new Set(playlistItems.map((item) => item.asset_id))] : picked.map((asset) => asset.id);
  const storageShortfalls = useStorageCheck(
    client,
    sendingAssetIds.length > 0 && recipients.length > 0
      ? { display_ids: recipients.map((target) => target.id), asset_ids: sendingAssetIds }
      : null,
  );
  const ready = byPlaylist ? pickedPlaylist !== null && playlistItems.length > 0 : picked.length > 0;
  const single = targets.length + skipped.length === 1 ? (targets[0] ?? skipped[0]) : null;
  const title = single ? `${single.name || '화면'} 에 바로 보내기` : `화면 ${targets.length + skipped.length}대에 바로 보내기`;

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2" ref={formRef}>
        {/* ── 왼쪽: 무엇을 보낼까 ─────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <div className="seg seg-sm flex w-full" role="group" aria-label="보낼 것">
            <button type="button" className="seg-btn flex-1" aria-pressed={!byPlaylist} onClick={() => setSource('content')}>
              콘텐츠
            </button>
            <button type="button" className="seg-btn flex-1" aria-pressed={byPlaylist} onClick={() => setSource('playlist')}>
              플레이리스트
            </button>
          </div>
          {byPlaylist ? (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="field-label">플레이리스트</span>
                <input
                  className="input input-search"
                  type="search"
                  placeholder="이름 검색"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <ul className="mt-2 max-h-80 overflow-y-auto rounded-lg border border-slate-200">
                {shownPlaylists.length === 0 ? (
                  <li className="px-3 py-4 text-center text-[12.5px] text-slate-400">
                    {playlists.loading ? '불러오는 중…' : query ? '찾는 플레이리스트가 없습니다' : '플레이리스트가 없습니다'}
                  </li>
                ) : null}
                {shownPlaylists.map((playlist) => (
                  <li key={playlist.id}>
                    <label className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50">
                      <input
                        type="radio"
                        name="direct-playlist"
                        checked={pickedPlaylist?.id === playlist.id}
                        onChange={() => setPickedPlaylist(playlist)}
                      />
                      <ListMusic className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate text-[13.5px] text-slate-700">{playlist.name}</span>
                      <span className="shrink-0 text-[11.5px] text-slate-400">항목 {playlist.playable_count}개</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="hint mt-1.5">고른 플레이리스트의 켜 둔 항목이 그 순서·노출 시간·전환 그대로 나갑니다.</p>
            </div>
          ) : (
          <>
          <div
            onDragOver={(event: DragEvent) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event: DragEvent) => {
              event.preventDefault();
              setDragging(false);
              addFiles(event.dataTransfer.files);
            }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              dragging ? 'border-th-5 bg-th-2/40' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <FileUp className="h-6 w-6 text-slate-400" strokeWidth={1.8} />
            {/* 터치 화면에는 끌어다 놓을 파일이 없다. 거기서는 고르는 단추 하나가 길이다. */}
            <p className="text-[13.5px] text-slate-600">
              <span className="[@media(hover:none)]:hidden">사진·영상·PDF 를 끌어다 놓으세요</span>
              <span className="hidden [@media(hover:none)]:inline">올릴 사진·영상·PDF 를 고르세요</span>
            </p>
            <label className="btn btn-tiny cursor-pointer">
              파일 선택
              <input
                type="file"
                multiple
                accept="image/*,video/*,application/pdf,.pdf"
                className="hidden"
                onChange={(event) => {
                  if (event.target.files) addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
            <p className="hint">올린 파일은 콘텐츠에 추가됩니다 · PDF 는 쪽마다 그림이 됩니다</p>
          </div>

          {uploading.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {uploading.map((name) => (
                <li key={name} className="flex items-center gap-2 text-[12.5px] text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                  <span className="truncate">{name}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="field-label">기존 콘텐츠</span>
              <input
                className="input input-search"
                type="search"
                placeholder="제목 검색"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            {/* 종류로 나눠 본다 — 콘텐츠가 쌓인 계정에서 "영상 중에서" 처럼 좁혀 고른다. */}
            <div className="seg seg-sm mt-2 flex w-full flex-wrap">
              {(Object.keys(KIND_LABEL) as Array<keyof typeof KIND_LABEL>).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={kind === value}
                  onClick={() => setKind(value)}
                  className="seg-btn flex-1 basis-14"
                >
                  {KIND_LABEL[value]}
                </button>
              ))}
            </div>
            <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200">
              {(library.data?.items ?? []).length === 0 ? (
                <li className="px-3 py-4 text-center text-[12.5px] text-slate-400">
                  {/*
                    거르고 있을 때는 무엇으로 걸렀는지 함께 말한다 — "콘텐츠가 없습니다" 만
                    보면 계정에 아무것도 없는 줄 알고 탭을 되돌릴 생각을 못 한다.
                  */}
                  {library.loading
                    ? '불러오는 중…'
                    : kind === 'all'
                      ? '콘텐츠가 없습니다'
                      : `${KIND_LABEL[kind]} 콘텐츠가 없습니다`}
                </li>
              ) : null}
              {(library.data?.items ?? []).map((asset) => (
                <li key={asset.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={pickedIds.has(asset.id)}
                      onChange={() => toggle(asset)}
                    />
                    <AssetIcon type={asset.type} />
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-slate-700">{asset.title}</span>
                    <span className="shrink-0 text-[11.5px] text-slate-400">
                      {asset.type === 'web' || asset.type === 'template' ? '—' : formatBytes(asset.size)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          </>
          )}
        </div>

        {/* ── 오른쪽: 어떻게 보낼까 ───────────────────────────── */}
        <div className="flex flex-col gap-3">
          {byPlaylist ? (
            <div>
              <span className="field-label">
                보낼 순서 · {playlistItems.length}개
                <HelpTip
                  text="플레이리스트의 켜 둔 항목이 그 순서대로 나갑니다. 보낸 뒤 플레이리스트를 고쳐도 이 바로 보내기는 그대로이고, 다시 보내면 반영됩니다. 보관했거나 아직 쓸 수 없는 콘텐츠는 빼고 보냅니다."
                  label="보낼 순서 도움말"
                />
              </span>
              {!pickedPlaylist ? (
                <p className="mt-2 rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-[12.5px] text-slate-400">
                  선택한 플레이리스트가 없습니다
                </p>
              ) : playlistItems.length === 0 ? (
                <p className="mt-2 rounded-lg border border-dashed border-amber-200 px-3 py-6 text-center text-[12.5px] text-amber-700">
                  「{pickedPlaylist.name}」 에 켜 둔 항목이 없습니다
                </p>
              ) : (
                <ol className="mt-2 flex max-h-80 flex-col gap-1.5 overflow-y-auto">
                  {playlistItems.map((item, index) => {
                    const asset = assetById.get(item.asset_id);
                    return (
                      <li key={`${item.asset_id}-${index}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                        <span className="w-4 shrink-0 text-center font-mono text-[11.5px] text-slate-400">{index + 1}</span>
                        {/* 이름을 받는 동안은 빈 칸 — '?'(지워진 콘텐츠) 로 보이지 않게. */}
                        {asset ? <AssetThumbSmall asset={asset} full className={ROW_THUMB} /> : <span className={ROW_THUMB} aria-hidden="true" />}
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-700">{asset?.title ?? '…'}</span>
                        <span className="shrink-0 text-[11.5px] text-slate-400">
                          {asset?.type === 'video' ? '끝까지' : `${item.dwell_sec}초`}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          ) : (
          <div>
            <span className="field-label">
              보낼 순서 · {picked.length}개
              <HelpTip
                text="고른 순서대로 재생합니다. 영상은 끝까지 재생한 뒤 다음으로 넘어갑니다."
                label="보낼 순서 도움말"
              />
            </span>
            {picked.length === 0 ? (
              <p className="mt-2 rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-[12.5px] text-slate-400">
                선택한 콘텐츠가 없습니다
              </p>
            ) : (
              <ol className="mt-2 flex flex-col gap-1.5">
                {picked.map((asset, index) => (
                  <li
                    key={asset.id}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5"
                  >
                    <span className="w-4 shrink-0 text-center font-mono text-[11.5px] text-slate-400">
                      {index + 1}
                    </span>
                    <AssetThumbSmall asset={asset} full className={ROW_THUMB} />
                    <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-700">{asset.title}</span>
                    <button
                      type="button"
                      className="link"
                      aria-label={`${asset.title} 빼기`}
                      onClick={() => toggle(asset)}
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>
          )}

          {/*
            노출 시간은 영상이 아닌 것에만 쓰인다 — 영상은 끝까지 재생한 뒤 넘어간다.

            모두 영상이면 칸을 없애고 이유를 대신 적는다. 칸을 남겨 두면 "15초로 보냈는데 왜 2분을
            트나" 하는 오해가 생긴다. 섞여 있으면 칸 아래에 영상은 따르지 않는다고 적는다.
          */}
          {byPlaylist ? (
            <p className="hint">노출 시간은 플레이리스트에 정한 대로입니다(영상은 끝까지).</p>
          ) : onlyVideo ? (
            <p className="hint">영상은 끝까지 재생됩니다.</p>
          ) : (
            <label className="field">
              <span className="field-label">한 장당 노출 시간(초)</span>
              <NumberInput rule={DWELL_RULE} value={dwell} invalid={showErrors && dwellError !== null} onChange={setDwell} />
              {showErrors && dwellError ? (
                <p className="field-error" role="alert">
                  {dwellError}
                </p>
              ) : someVideo ? (
                <small className="hint">영상은 이 값과 상관없이 끝까지 재생됩니다.</small>
              ) : null}
            </label>
          )}

          <div className="field">
            <span className="field-label">재생 기간</span>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(UNTIL_LABEL) as UntilChoice[]).map((choice) => (
                <button
                  key={choice}
                  type="button"
                  aria-pressed={until === choice}
                  onClick={() => setUntil(choice)}
                  className="btn btn-tiny"
                >
                  {UNTIL_LABEL[choice]}
                </button>
              ))}
            </div>
            {until === 'custom' ? (
              <div className="mt-2">
                <DateTimeField label="끝낼 때" defaultTime="23:59" value={customUntil} onChange={setCustomUntil} />
              </div>
            ) : null}
            <small className="hint">
              {until === 'forever'
                ? '해제하기 전까지 원래 편성이 가려집니다.'
                : '기간이 끝나면 원래 편성으로 돌아갑니다.'}
            </small>
          </div>

          {/* 메모는 선택 — [메모] 를 눌러야 칸이 나온다(점진적 공개). */}
          <Disclosure title="메모" summary={note || '보내는 이유 (선택)'}>
            <label className="field">
              <span className="field-label sr-only">메모</span>
              <input
                className="input"
                maxLength={200}
                placeholder="보내는 이유 (선택)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
          </Disclosure>

          {skipped.length > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900" role="status">
              <p>
                꺼진 화면 {skipped.length}대:{' '}
                {skipped
                  .slice(0, 3)
                  .map((display) => display.name || '(이름 없음)')
                  .join(', ')}
                {skipped.length > 3 ? ` 외 ${skipped.length - 3}대` : ''}
              </p>
              <label className="mt-1.5 flex items-start gap-2">
                <input type="checkbox" checked={queueOffline} onChange={(event) => setQueueOffline(event.target.checked)} />
                <span>
                  꺼진 화면에도 걸어 두기 — 다시 연결되면(끝낼 시각 전이면) 나갑니다. 그 전까지 화면 상세에 ‘전달 대기’ 로
                  보입니다.
                </span>
              </label>
            </div>
          ) : null}

          {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
          <StorageWarning shortfalls={storageShortfalls} />

          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
              취소
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={action.pending || !ready || sendCount === 0 || (until === 'custom' && !customUntil)}
              onClick={send}
            >
              {action.pending ? '보내는 중…' : `보내기 (${sendCount}대)`}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** 지금 직접 전송 중임을 알리는 띠. 화면 상세 맨 위에 둔다. */
export function DirectBanner({
  note,
  expiresAt,
  createdAt,
  deliveredAt,
  pending,
  onClear,
}: {
  note: string;
  expiresAt: string | null;
  createdAt: string;
  /** 화면이 받아 간 시각. null 이면 전달 대기(꺼진 화면에 걸어 둔 것). 옛 서버는 undefined. */
  deliveredAt?: string | null | undefined;
  pending: boolean;
  /** 해제. 권한이 없으면 주지 않는다 — 그때는 단추가 나오지 않는다. */
  onClear?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-th-6 bg-th-2/60 px-4 py-3">
      {deliveredAt === null ? (
        <Badge tone="warn">바로 보내기 · 전달 대기</Badge>
      ) : (
        <Badge tone="brand">바로 보내기 재생 중</Badge>
      )}
      <span className="text-[13.5px] text-slate-700">
        {note || '평소 편성 대신 재생 중입니다'}
      </span>
      <span className="text-[12.5px] text-slate-500">
        <RelTime iso={createdAt} /> 시작 ·{' '}
        {expiresAt ? `${relativeTime(expiresAt)} 종료` : '해제할 때까지'}
      </span>
      {onClear ? (
        <button type="button" className="btn btn-tiny ml-auto" disabled={pending} onClick={onClear}>
          해제
        </button>
      ) : null}
    </div>
  );
}

/**
 * 보낼 순서 줄의 썸네일 칸(2026-11-03) — 고른 것이 무엇인지 제목만이 아니라 내용으로 알아보게, 편성 줄(64px)보다 크게
 * 웹 페이지·HTML 까지 줄여 그린다. 급히 보내는 창이라 잘못 고른 것을 보내기 전에 눈으로 걸러야 한다.
 */
const ROW_THUMB = 'h-[54px] w-24 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-100';

/** 종류마다 다른 그림. 목록을 훑을 때 제목보다 먼저 읽힌다 — 콘텐츠 화면과 같은 그림을 쓴다. */
function AssetIcon({ type }: { type: Asset['type'] }): ReactNode {
  const Icon =
    type === 'video' ? Video : type === 'template' ? FileText : type === 'web' ? Play : type === 'html' ? FileCode : ImageIcon;
  return <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={1.9} />;
}

/**
 * 선택지를 실제 시각으로 바꾼다.
 *
 * 계산을 화면에서 하는 이유: 사용자가 "오늘 자정"이라고 할 때의 오늘은 **보는
 * 사람의 오늘**이다. 서버 타임존으로 계산하면 다른 날이 될 수 있다.
 */
/**
 * 전송에 실을 노출 시간.
 *
 * **모두 영상이면 싣지 않는다** — 칸을 감췄으니 값도 보내지 않는다(감춘 칸의 값이 어딘가에 쓰이면 감춘
 * 의미가 없다). 섞여 있으면 싣는다: 사진·템플릿·웹이 그 값을 쓰고, 영상은 화면이 끝까지 튼다(서버도 옛
 * 화면을 위해 영상의 노출 시간을 길이 이상으로 올려 보낸다 — core `videoDwellSec`).
 */
/** 노출 시간 칸 — 서버(`directSchema.dwell_sec`)와 같은 범위. */
const DWELL_RULE: NumberRule = { label: '노출 시간', min: 1, max: 3600, unit: '초' };

export function directDwell(
  picked: readonly ContentKind[],
  dwell: number,
): { dwell_sec?: number } {
  return allVideo(picked) ? {} : { dwell_sec: dwell };
}

export function resolveUntil(choice: UntilChoice, custom: string, now: Date = new Date()): string | null {
  if (choice === 'forever') return null;
  if (choice === 'custom') return custom ? new Date(custom).toISOString() : null;
  if (choice === '1h') return new Date(now.getTime() + 3_600_000).toISOString();
  if (choice === '4h') return new Date(now.getTime() + 4 * 3_600_000).toISOString();

  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.toISOString();
}
